/**
 * Records every flow-emitted payload so the Maestro suite can assert analytics values and
 * scan for un-interpolated Liquid (NAM-4373). Test-app only — mirrors FlowTranscript.swift.
 *
 * Posts the WHOLE record array on every append rather than streaming deltas: a run that
 * crashes mid-flow still leaves everything recorded up to the crash, and the sink stays a
 * pure overwrite with no server-side merge to get wrong.
 */
const ROUTE = '/__flow-transcript';

// The sink is a dev-server middleware (`apply: 'serve'`), so outside `vite dev` there is nothing
// listening. Guarding on DEV keeps the recorder from firing a POST per flow event in the Tizen /
// webOS / Cloudflare Pages bundles, where it would be pure noise. `vite build` constant-folds this
// to false for all three targets, so the whole class drops out of production output.
const ENABLED = import.meta.env.DEV;

// 'paywall' (NAM-5123): a SHOW_PAYWALL raise; a page re-raised after its flow exits skips
// __appear__, so no flow event marks it.
type TranscriptKind = 'event' | 'handoff' | 'paywall';

class FlowTranscript {
  private records: Array<Record<string, unknown>> = [];
  private seq = 0;

  /** Called once at app load so each Maestro test case starts clean. */
  reset(): void {
    if (!ENABLED) return;
    this.records = [];
    this.seq = 0;
    void this.flush();
  }

  record(kind: TranscriptKind, payload: unknown): void {
    if (!ENABLED) return;
    const base: Record<string, unknown> = { seq: this.seq, kind };
    // Merge the payload's own keys at the record root, matching the Apple/Android
    // transcript shape the assert engine reads (record.utag_data, record.click_action).
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      Object.assign(base, payload);
    } else {
      base.value = payload;
    }
    this.records.push(base);
    this.seq += 1;
    void this.flush();
  }

  private async flush(): Promise<void> {
    try {
      await fetch(ROUTE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.records),
      });
    } catch {
      // The sink only exists under `vite dev`. Outside an e2e run there is nothing
      // listening, and a demo app must not break because of it.
    }
  }
}

export const flowTranscript = new FlowTranscript();
